import React, { useEffect, useState } from "react";
import {
  Popconfirm,
  Empty,
  Spin,
  Drawer,
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Col,
  message,
} from "antd";
import { clearLiabilityState } from "../../../../Store/Slices/Finance/liabilities/liabilities.slice";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteLiabilityThunk,
  fetchLiabilitiesThunk,
  fetchSingleLiabilityThunk,
} from "../../../../Store/Slices/Finance/liabilities/liabilities.thunk";
import LiabilityDrawer from "./LiabilityDrawer";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FiEye, FiEdit2, FiTrash2 } from "react-icons/fi";
import { MdOutlinePayment } from "react-icons/md";
import PayLiability from "./PayLiability";
import Sidebar from "../../../../Components/Common/Sidebar";
import toast from "react-hot-toast";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction.js";
import { PERMISSIONS } from "../../../../config/permission";

const { Option } = Select;
const defaultDrawer = { open: false, mode: "add", record: null };

const LiabilitiesTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    liabilities = [],
    loading,
    error,
    success,
    message: successMessage,
  } = useSelector((state) => state.admin.liabilities);

  const { loading: expenseLoading } = useSelector(
    (store) => store.admin.operationalExpenses,
  );

  const { activeYear } = useSelector((store) => store.common.financialYear);
  const minDate = dayjs(activeYear?.startDate?.slice(0, 10));
  const maxDate = dayjs(activeYear?.endDate?.slice(0, 10));

  const [drawer, setDrawer] = useState(defaultDrawer);
  const [payDrawerOpen, setPayDrawerOpen] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState(null);
  const [payForm] = Form.useForm();
  const [paymentStatus] = useState("paid");
  const [paymentType, setPaymentType] = useState("cash");

  useEffect(() => {
    dispatch(fetchLiabilitiesThunk());
  }, [dispatch]);


  const openPayDrawer = (record = {}) => {
    setSelectedLiability(record);
    payForm.resetFields();
    setPaymentType("cash");
    setPayDrawerOpen(true);
  };


  const handleDelete = async (id) => {
    try {
      await dispatch(deleteLiabilityThunk(id)).unwrap();

      toast.success("Liability deleted successfully");

      dispatch(fetchLiabilitiesThunk()); // refresh list

    } catch (err) {
      toast.error(err || "Failed to delete liability");
    }
  };

  const showPaid = paymentStatus === "paid" || paymentStatus === "partial";
  const showCheque = paymentStatus === "paid" && paymentType === "cheque";

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col gap-5">
      <div className="flex justify-between items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div>
          <h1 className="page-heading">Liabilities Management</h1>
          <p className="subheading">
            Track and manage all financial liabilities
          </p>
        </div>

        <ProtectedAction requiredPermission={PERMISSIONS.ADD_LIABILITY}>
          <button
            className="flex items-center gap-2 px-4 py-2 btn-gradient"
            onClick={() => setDrawer({ open: true, mode: "add", record: null })}
          >
            <PlusOutlined /> Add Liability
          </button>
        </ProtectedAction>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spin size="large" />
        </div>
      ) : !liabilities?.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-20">
          <Empty description="No liabilities found" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {liabilities.filter(Boolean).map((record, index) => {
            const safeRecord = record || {};

            return (
              <div
                key={safeRecord?._id || index}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-100 transition-all duration-200 flex flex-col overflow-hidden"
              >
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 capitalize leading-tight truncate">
                        {safeRecord?.title || "Untitled Liability"}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {safeRecord?.entityId?.entityName || "—"}
                      </p>
                    </div>

                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border flex-shrink-0 ${safeRecord?.status === "Closed"
                        ? "bg-green-50 text-green-600 border-green-200"
                        : "bg-blue-50 text-blue-600 border-blue-100"
                        }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${safeRecord?.status === "Closed"
                          ? "bg-green-500"
                          : "bg-blue-500"
                          }`}
                      />
                      {safeRecord?.status || "Active"}
                    </span>
                  </div>
                </div>

                <div className="p-5 flex flex-col gap-3 flex-1">
                  {[
                    {
                      label: "Principal",
                      value: Number(
                        safeRecord?.principalAmount || 0,
                      ).toLocaleString(),
                    },
                    {
                      label: "EMI Amount",
                      value: Number(safeRecord?.emiAmount || 0).toFixed(2),
                    },
                    {
                      label: "Total Payable",
                      value: Number(
                        safeRecord?.totalPayableAmount || 0,
                      ).toFixed(2),
                    },
                    {
                      label: "Remaining",
                      value: Number(safeRecord?.remainingAmount || 0).toFixed(
                        2,
                      ),
                      highlight: true,
                    },
                    {
                      label: "Total Paid",
                      value: Number(safeRecord?.totalPaid || 0).toFixed(2),
                    },
                    {
                      label: "Interest Rate",
                      value: `${safeRecord?.interestRate || 0}% (${safeRecord?.interestType || "—"
                        })`,
                    },
                    {
                      label: "Frequency",
                      value: safeRecord?.frequency || "—",
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between"
                    >
                      <span className="text-xs text-gray-400">{row.label}</span>
                      <span
                        className={`text-sm font-semibold ${row.highlight ? "text-purple-600" : "text-gray-800"
                          }`}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-2 flex-wrap">

                  <ProtectedAction requiredPermission={PERMISSIONS.PAY_LIABILITY}>
                    {safeRecord?.status !== "Closed" && (
                      <button
                        onClick={() => openPayDrawer(safeRecord)}
                        className="flex items-center gap-1.5 px-3 py-1.5 btn-gradient"
                      >
                        <MdOutlinePayment className="w-3.5 h-3.5" />
                        Pay
                      </button>
                    )}
                  </ProtectedAction>

                  <button
                    onClick={() => {
                      if (!safeRecord?._id) return;
                      dispatch(fetchSingleLiabilityThunk(safeRecord._id));
                      setDrawer({
                        open: true,
                        mode: "view",
                        record: safeRecord,
                      });
                    }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition"
                  >
                    <FiEye className="w-4 h-4" />
                  </button>
                  <ProtectedAction requiredPermission={PERMISSIONS.EDIT_LIABILITY} >

                    <button
                      onClick={() =>
                        setDrawer({
                          open: true,
                          mode: "edit",
                          record: safeRecord,
                        })
                      }
                      className="p-1.5 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-50 transition"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>

                  </ProtectedAction>
                  <ProtectedAction requiredPermission={PERMISSIONS.DELETE_LIABILITY}>
                    <Popconfirm
                      title="Delete this liability?"
                      onConfirm={() => safeRecord?._id && handleDelete(safeRecord._id)}
                      okButtonProps={{ danger: true }}
                    >
                      <button className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition">
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </Popconfirm>
                  </ProtectedAction>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sidebar
        title={
          <div>
            <p className="text-base font-bold text-gray-800">
              {drawer.mode === "add"
                ? "Create Liability"
                : drawer.mode === "edit"
                  ? "Edit Liability"
                  : "View Liability"}
            </p>
          </div>
        }
        isOpen={drawer.open}
        onClose={() => setDrawer({ open: false, mode: "add", record: null })}
        width={700}
      >
        <LiabilityDrawer drawer={drawer} setDrawer={setDrawer} />
      </Sidebar>

      <Sidebar
        title={
          <div>
            <p className="text-base font-bold text-gray-800">Pay Liability</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {selectedLiability?.title} — EMI:{" "}
              {selectedLiability?.emiAmount || 0}
            </p>
          </div>
        }
        isOpen={payDrawerOpen}
        onClose={() => setPayDrawerOpen(false)}
        width={600}
      >
        <PayLiability
          open={payDrawerOpen}
          onClose={() => setPayDrawerOpen(false)}
          liability={selectedLiability}
        />
      </Sidebar>
    </div>
  );
};

export default LiabilitiesTable;
